/**
 * My Provider Relationship Data Model
 * Based on PACE "My Provider" relationship specifications
 */

export const createMyProviderRelationship = (data = {}) => {
  return {
    id: data.id || null,
    participantId: data.participantId || null,
    providerId: data.providerId || null,
    providerName: data.providerName || '',
    
    // Relationship Type
    relationshipType: data.relationshipType || null, // My Provider, Plan Manager, Support Coordinator, Recovery Coach
    
    // Lifecycle
    startDate: data.startDate || null, // DateTime - Claims before this rejected
    endDate: data.endDate || null, // DateTime - Triggers read-only/archived state
    status: data.status || 'active', // active, expired, revoked, pending
    
    // Consent Scope
    consentScope: data.consentScope || {
      coreSupports: true,
      capacityBuilding: false,
      capital: false,
      dailyLife: false,
      specificLineItems: [] // Array of specific line item codes
    },
    
    // Access Permissions
    canViewBudget: data.canViewBudget ?? true,
    canSubmitClaims: data.canSubmitClaims ?? true,
    canViewPlan: data.canViewPlan ?? true,
    canViewClinicalNotes: data.canViewClinicalNotes ?? false,
    
    // API Sync
    lastSyncedAt: data.lastSyncedAt || null,
    syncStatus: data.syncStatus || 'pending',
    
    // Expiry Management
    expiryWarningSent: data.expiryWarningSent || {
      days30: false,
      days14: false,
      days7: false
    },
    
    // Metadata
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString(),
    createdBy: data.createdBy || null
  };
};

export const RELATIONSHIP_TYPES = {
  MY_PROVIDER: 'My Provider',
  PLAN_MANAGER: 'Plan Manager',
  SUPPORT_COORDINATOR: 'Support Coordinator',
  RECOVERY_COACH: 'Recovery Coach'
};

export const RELATIONSHIP_STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
  PENDING: 'pending'
};

export const isRelationshipActive = (relationship) => {
  if (!relationship) return false;
  if (relationship.status !== 'active') return false;
  
  const now = new Date();
  const startDate = relationship.startDate ? new Date(relationship.startDate) : null;
  const endDate = relationship.endDate ? new Date(relationship.endDate) : null;
  
  if (startDate && now < startDate) return false;
  if (endDate && now > endDate) return false;
  
  return true;
};

export const getDaysUntilExpiry = (relationship) => {
  if (!relationship.endDate) return null;
  
  const endDate = new Date(relationship.endDate);
  const now = new Date();
  const diffTime = endDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

export const shouldSendExpiryWarning = (relationship) => {
  const daysUntilExpiry = getDaysUntilExpiry(relationship);
  if (!daysUntilExpiry || daysUntilExpiry < 0) return null;
  
  const warnings = {
    days30: daysUntilExpiry <= 30 && daysUntilExpiry > 14 && !relationship.expiryWarningSent.days30,
    days14: daysUntilExpiry <= 14 && daysUntilExpiry > 7 && !relationship.expiryWarningSent.days14,
    days7: daysUntilExpiry <= 7 && daysUntilExpiry > 0 && !relationship.expiryWarningSent.days7
  };
  
  return warnings.days30 || warnings.days14 || warnings.days7 ? warnings : null;
};

